import { v4 as uuidv4 } from 'uuid';
import * as R from 'ramda';

import { GivexCard } from 'plugins/givexHeartland/types';

import { defaultGivexUrl } from '../configuration/Configuration';

class GivexError extends Error {
  canRetry: boolean;

  constructor(message: string, { canRetry }: { canRetry: boolean }) {
    super(message);
    this.canRetry = canRetry;
  }
}

const errorForResponse = msg => {
  const getError = (code, message) => {
    const errTxt = {
      2: 'The request to GIVEX has timed out',
      3: 'Connection to GIVEX could not be established',
      4: 'MS Config error: no such host',
    };
    if (code) {
      switch (code) {
        case 2:
        case 3:
        case 4:
          return new GivexError(errTxt[code], { canRetry: true });
        case 1:
        default:
          return new GivexError(message, { canRetry: false });
      }
    }
    return null;
  };

  const baseErr = getError(Number(msg.statusCode), msg.statusMessage);
  if (baseErr) {
    return baseErr;
  }
  if (!msg.data) {
    return new GivexError(
      'Invalid response from MS (statusCode 0 but no data)',
      {
        canRetry: false,
      },
    );
  }
  return getError(Number(msg.data.statusCode), msg.data.statusMessage);
};

const stripUndefineds = obj =>
  Object.fromEntries(Object.entries(obj).filter(([, b]) => b !== undefined));

let givexURL = defaultGivexUrl;
export const updateGivexURL = (newURL: string) => {
  givexURL = newURL;
};
const makeRequest = <T>(req, data) =>
  new Promise<{ data: T; id: string }>((resolve, reject) => {
    // Open WS only when processing on desktop - on wrappers, communications are done via messageChannel
    if (!window.wrapperInfo) {
      const ws = new window.WebSocket(givexURL);

      ws.onopen = () =>
        ws.send(
          JSON.stringify({
            id: uuidv4(),
            function: req,
            data: stripUndefineds(data),
          }),
        );
      // eslint-disable-next-line no-multi-assign
      ws.onclose = ws.onerror = () =>
        reject(
          new Error(
            'No connection to Givex Microservice - Make sure that it is running',
          ),
        );

      ws.onmessage = rawMsg => {
        const msg = JSON.parse(rawMsg.data);
        const err = errorForResponse(msg);

        if (err) {
          reject(err);
        } else {
          resolve(msg);
        }
      };
    } else {
      const uuid = uuidv4();

      window.wrapperMessageChannel.port1.postMessage({
        requestID: uuid,
        type: `payment:${req}`,
        provider: 'payment-givex',
        payload: stripUndefineds(data),
      });
      window.wrapperMessageChannel.port1.onmessage = rawMsg => {
        const msg = rawMsg.data;
        const err = errorForResponse(msg);

        if (err) {
          reject(err);
        } else {
          resolve(msg);
        }
      };
    }
  });

/**
 * Fetch balance and expiration information about the given card
 */
export const getBalance = (card: GivexCard) =>
  makeRequest<{
    statusCode: '0' | string;
    statusMessage: 'SUCCESS' | string;
    transactionType: 'Balance';
    cardNumber: string | '603628-257971249';
    cardType: string | 'GIFTCARD';
    certificateBalance: string | '50.00';
    certificateExpirationDate: string | 'None';
    currencyCode: string | 'CAD';
    activatingCurrencyCode: string | 'CAD';
  }>('balance', { cardno: card.cardNo, securityCode: card.pin }).then(msg =>
    R.evolve({
      certificateExpirationDate: d => (d === 'None' ? null : d),
    })(msg.data),
  );

/**
 * Fetch edit history for the given card (activations, transactions and adjustments).
 *
 * NB: Cancelled charges are removed from history, rather than listing both charge and cancellation
 */
export const getHistory = (card: GivexCard) =>
  makeRequest<{
    statusCode: '0' | string;
    statusMessage: 'SUCCESS' | string;
    transactionType: string;
    cardNumber: string;
    cardType: 'GIFTCARD';
    certificateBalance: string;
    certificateExpirationDate: 'None' | string;
    currencyCode: 'CAD' | string;
    totalRows: '5' | string;
    transactionHistory: {
      transactions: {
        amount: '-1.37 (CAD -1.80)' | string;
        customer: 'ERPLY' | string;
        date: '2020-08-31' | string;
        givexMethod: 'Redeem' | string;
        time: '02:41:22' | string;
      }[];
    };
  }>('history', {
    cardno: card.cardNo,
    securityCode: card.pin,
    historyType: 'Certificate',
  }).then(msg => msg.data);

/**
 * Add money to a gift card, creating it if it does not already exist
 */
export const refillGiftcard = (card: GivexCard, sum: string | number) =>
  makeRequest<{
    statusCode: '0' | string;
    statusMessage: 'SUCCESS' | string;
    transactionType: string;
    cardNumber: string;
    cardType: 'GIFTCARD';
    referenceNumber: string;
    certificateBalance: string;
    certificateExpirationDate: 'None' | string;
  }>('activateOrIncrement', {
    cardno: card.cardNo,
    sum: Math.abs(Number(sum)).toFixed(2),
    securityCode: card.pin,
  }).then(msg => ({ ...msg.data, referenceNumber: msg.id }));

export const adjustGiftCard = (card: GivexCard, sum) =>
  makeRequest('adjustment', {
    cardno: card.cardNo,
    sum: String(sum),
    securityCode: card.pin,
  }).then(msg => msg.data);

/**
 * Charge (make a payment with) a gift card, subtracting balance from it
 */
export const chargeGiftcard = (card: GivexCard, sum: string | number) =>
  makeRequest<{
    statusCode: '0';
    statusMessage: 'SUCCESS';
    transactionType: 'Payment';
    cardNumber: '603628115972579712522';
    cardType: 'GIFTCARD';
    authCode: '943547';
    sum: '19.10';
    certificateBalance: '2064.39';
    certificateExpirationDate: 'None';
  }>('Payment', {
    cardno: card.cardNo,
    sum: Math.abs(Number(sum)).toFixed(2),
    securityCode: card.pin,
  }).then(msg => msg.data);

/**
 * Cancel a previous charge - this removes the charge from the card's history, unlike {@link refillGiftcard} which would be listed as a second transaction
 */
export const cancelGiftcardCharge = (card: GivexCard, sum: string | number) =>
  makeRequest<{
    statusCode: '0';
    statusMessage: 'SUCCESS';
    transactionType: 'Cancel';
    cardNumber: '603628-257971251' | string;
    cardType: 'GIFTCARD' | string;
    authCode: '943653' | string;
    sum: '20' | string;
    certificateBalance: '100.00' | string;
    certificateExpirationDate: 'None';
  }>('Cancel', {
    cardno: card.cardNo,
    sum: Math.abs(Number(sum)).toFixed(2),
    securityCode: card.pin,
  }).then(msg => msg.data);

/**
 * Cancel a previous refill - Givex doesn't have a way to cancel refills, so this is just the chargeGiftcard method to achieve a similar end result
 */
export const reverseGiftcardRefill = (card, sum) => chargeGiftcard(card, sum);

/**
 * Transfer the entire balance of one card onto another
 *
 * Used when switching cards (for example to trade a pinless card for a pin card)
 */
export const transferBalance = (from: GivexCard, to: GivexCard) =>
  makeRequest<{
    statusCode: string;
    statusMessage: string;
    transactionType: 'BalanceTransfer';
    cardType: 'GIFTCARD' | string;
    cardNumberTransferTo: string;
    sum: string;
    securityCode: string;
    certificateBalance: '100.00' | string;
    certificateExpirationDate: 'None';
    currencyCode: string;
    cardNumber: string;
    authCode: string;
  }>('balanceTransfer', {
    cardno: from.cardNo,
    cardnoTransferTo: to.cardNo,
    securityCode: from.pin,
  }).then(msg => msg.data);
